import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Search, BookOpen } from "lucide-react";
import { API } from "../lib/api";

/* /precedents — assessor-facing browser for the institutional-memory corpus.
 *
 * Every decision an RHE assessor makes (per-DM Form 2 verdict, per-defect
 * EW verdict, ledger override) writes one record here. The DRFA Expert
 * retrieves matching records at chat time and cites them. This page is the
 * raw audit surface — a Programme Director, NSWRA reviewer, or new joiner
 * can see every precedent at a glance and filter by portal type, decision
 * kind, or free-text query.
 */
export function Precedents() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [portalFilter, setPortalFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (query.trim()) params.q = query.trim();
      if (portalFilter) params.portal_type = portalFilter;
      if (kindFilter) params.decision_kind = kindFilter;
      const [list, st] = await Promise.all([
        axios.get(`${API}/precedents`, { params }),
        axios.get(`${API}/precedents/stats`),
      ]);
      setRows(list.data || []);
      setStats(st.data || null);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [query, portalFilter, kindFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="precedents-page">
      <div className="border-b border-slate-200 pb-5">
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-700 mb-2 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5"/> Institutional memory
        </div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Precedent corpus</h1>
        <p className="text-base text-slate-600 mt-2 max-w-3xl leading-relaxed">
          Every assessor decision feeds into this corpus — per-DM Form 2 verdicts,
          per-defect EW verdicts, and per-line ledger overrides with their rationale.
          The DRFA Expert retrieves matching records at chat time and cites them. This
          page is the audit surface.
        </p>
        <div className="mt-4 rounded-md border border-blue-200 bg-blue-50/40 p-3 text-[12.5px] text-slate-700 leading-relaxed max-w-3xl" data-testid="precedents-trajectory-banner">
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-blue-700 mb-1">Why this matters</div>
          The DRFA Agent is the endgame. Every decision you record here is training signal
          for the retrieval-and-reasoning layer. Today the SQP decides every line. As the
          corpus matures the Agent will progressively take on more of the SQP role &mdash;
          and on the trajectory the DRFA Agent <b>becomes the SQP</b>, feeding every
          assessment process end-to-end. {" "}
          <Link to="/security" className="text-blue-600 underline">Read the full trajectory →</Link>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="precedent-stats">
          <Stat label="Total precedents" value={stats.total} />
          <Stat label="Last 24 hours" value={stats.last_24h} />
          <Stat label="DM verdicts" value={stats.by_kind?.dm_verdict || 0} />
          <Stat label="Defect verdicts" value={stats.by_kind?.defect_verdict || 0} />
          <Stat label="Ledger lines" value={stats.by_kind?.ledger_line || 0} />
        </div>
      )}

      <div className="card p-4 flex flex-wrap gap-3 items-end" data-testid="precedent-filters">
        <div className="flex-1 min-w-[220px]">
          <label className="label-upper block mb-1">Search</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
            <input
              className="input pl-9"
              placeholder="e.g. spray paint, on-cost, scope creep…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") load(); }}
              data-testid="precedent-search-input"
            />
          </div>
        </div>
        <div>
          <label className="label-upper block mb-1">Portal</label>
          <select className="input" value={portalFilter} onChange={e => setPortalFilter(e.target.value)} data-testid="precedent-portal-filter">
            <option value="">All portals</option>
            <option value="EW Finalisation">EW Finalisation</option>
            <option value="IRW Scope Approval">IRW Scope Approval</option>
            <option value="IRW Finalisation">IRW Finalisation</option>
            <option value="EPAR Scope Approval">EPAR Scope Approval</option>
            <option value="EPAR Finalisation">EPAR Finalisation</option>
          </select>
        </div>
        <div>
          <label className="label-upper block mb-1">Decision kind</label>
          <select className="input" value={kindFilter} onChange={e => setKindFilter(e.target.value)} data-testid="precedent-kind-filter">
            <option value="">All kinds</option>
            <option value="dm_verdict">DM verdict</option>
            <option value="defect_verdict">Defect verdict</option>
            <option value="ledger_line">Ledger line</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-12 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card p-8 text-center text-slate-500" data-testid="precedent-empty-state">
          <BookOpen className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
          {query || portalFilter || kindFilter
            ? "No precedents match the current filters."
            : "No precedents yet. Decisions you make in the inbox will start populating this corpus."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="precedent-list">
          {rows.map(p => <PrecedentCard key={p.id} p={p}/>)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="card p-4">
      <div className="label-upper mb-1">{label}</div>
      <div className="font-heading text-2xl font-bold tabular">{value ?? "—"}</div>
    </div>
  );
}

function PrecedentCard({ p }) {
  const decisionTone = ({
    Yes: "bg-emerald-100 text-emerald-800",
    No: "bg-red-100 text-red-700",
    RFI: "bg-amber-100 text-amber-800",
    eligible: "bg-emerald-100 text-emerald-800",
    ineligible: "bg-red-100 text-red-700",
    review: "bg-amber-100 text-amber-800",
  })[p.decision] || "bg-slate-100 text-slate-700";
  return (
    <div className="card p-4 flex gap-4" data-testid={`precedent-row-${p.id}`}>
      <div className="shrink-0 w-24 text-center">
        <div className={`inline-block px-2 py-0.5 rounded-full text-[10px] tracking-[0.14em] uppercase font-bold ${decisionTone}`}>
          {p.decision}
        </div>
        <div className="text-[10px] text-slate-500 font-mono mt-1">{p.decision_kind}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-slate-900">{p.line_text || "—"}</div>
        {p.reason && <div className="text-[12px] text-slate-700 italic mt-1">&ldquo;{p.reason}&rdquo;</div>}
        <div className="text-[10.5px] text-slate-500 font-mono mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>{p.assessor_email || "anonymous"}</span>
          <span>{(p.created_at || "").slice(0, 16).replace("T", " ")}</span>
          {p.portal_type && <span>{p.portal_type}</span>}
          {p.dm && <span>{p.dm}</span>}
          {p.amount != null && <span>${Number(p.amount).toLocaleString()}</span>}
          {p.job_id && (
            <Link to={`/inbox/${p.job_id}`} className="text-blue-600 hover:underline" data-testid={`precedent-job-link-${p.id}`}>
              {p.job_top_dir || p.job_id} →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
